import axios from 'axios';
import { contractorAuth } from './contractorAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Talking to the links endpoints (routes/links.js, migration 070).
 *
 * One shelf for the whole firm: staff and administrators read and change
 * it, a contractor reads it on their portal token (contractorAuth) and every
 * write from them is a 403 - the page hides the controls, the server is what
 * makes that true. Everybody else's request carries the Entra token through
 * the interceptor in lib/apiClient.js.
 *
 * Until 070 has run every call answers 503 naming the migration;
 * `notReady` recognises that so the page can say so calmly.
 */

const cfg = (extra = {}) => ({ ...(contractorAuth() || {}), ...extra });

export const loadLinks = () => axios.get(`${API_BASE}/links`, cfg()).then((r) => r.data);

export const addPage = (body) => axios.post(`${API_BASE}/links/pages`, body, cfg()).then((r) => r.data);
export const changePage = (id, body) => axios.patch(`${API_BASE}/links/pages/${id}`, body, cfg()).then((r) => r.data);
export const deletePage = (id) => axios.delete(`${API_BASE}/links/pages/${id}`, cfg()).then((r) => r.data);

export const addSection = (pageId, body) => axios.post(`${API_BASE}/links/pages/${pageId}/sections`, body, cfg()).then((r) => r.data);
export const changeSection = (id, body) => axios.patch(`${API_BASE}/links/sections/${id}`, body, cfg()).then((r) => r.data);
export const deleteSection = (id) => axios.delete(`${API_BASE}/links/sections/${id}`, cfg()).then((r) => r.data);

export const addLink = (sectionId, body) => axios.post(`${API_BASE}/links/sections/${sectionId}/links`, body, cfg()).then((r) => r.data);
export const changeLink = (id, body) => axios.patch(`${API_BASE}/links/items/${id}`, body, cfg()).then((r) => r.data);
export const deleteLink = (id) => axios.delete(`${API_BASE}/links/items/${id}`, cfg()).then((r) => r.data);

export const restore = (entity, id) => axios.post(`${API_BASE}/links/restore`, { entity, id }, cfg()).then((r) => r.data);

/** The record, newest first. `before` is the `next` of the previous page. */
export const loadLog = ({ entity = '', q = '', before = '' } = {}) => axios
  .get(`${API_BASE}/links/log`, cfg({ params: { entity: entity || undefined, q: q || undefined, before: before || undefined, limit: 40 } }))
  .then((r) => r.data);

/**
 * One run of the reorganisation (082) or its undo, which History shows as a
 * single entry: its changes, oldest first. `after` is the `next` of the page before.
 */
export const loadLogRun = (id, { entity = '', q = '', after = '' } = {}) => axios
  .get(`${API_BASE}/links/log`, cfg({ params: { run: id, entity: entity || undefined, q: q || undefined, after: after || undefined, limit: 100 } }))
  .then((r) => r.data);

/** Put the shelf back as it was before 082 (administrators, once): what it did, and what it left. */
export const undoReorg = () => axios.post(`${API_BASE}/links/reorg/undo`, {}, cfg()).then((r) => r.data);

export const loadTrash = () => axios.get(`${API_BASE}/links/trash`, cfg()).then((r) => r.data?.items || []);

/** Before migration 070: a 503 naming it. */
export const notReady = (error) => error?.response?.status === 503
  && /migrate:070/.test(error?.response?.data?.error || '');

/** The sentence the server sent, or a plain one. */
export const said = (error, fallback) => error?.response?.data?.error || fallback;

/** The scheme of an address, lower-case, or null. */
export const schemeOf = (url) => ((/^([a-z][a-z0-9+.-]*):/i.exec(String(url || '').trim()) || [])[1] || '').toLowerCase() || null;

/**
 * What somebody typed into the address box, made into an address: "bov.com"
 * becomes "https://bov.com". Anything with a scheme is left for the server to
 * judge - this only saves typing, it never decides what is allowed.
 */
export function tidyUrl(raw) {
  const t = String(raw || '').trim();
  if (!t) return t;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t) && !/^[^:]+\.[a-z]{2,}(:\d+)?(\/|$)/i.test(t)) return t;
  if (/^\/\//.test(t)) return `https:${t}`;
  if (/^[^\s/]+\.[a-z]{2,}/i.test(t)) return `https://${t}`;
  return t;
}

/** The host of a web address, without www., for the line under a title. */
export function hostOf(url) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * What this browser runs on, as far as the links care: 'windows', 'mac' or
 * 'other'. A Windows link shows only on 'windows', a Mac link only on 'mac',
 * and 'other' - a phone, a tablet, Linux, a Chromebook - gets neither, since
 * neither kind of app opens there.
 *
 * userAgentData.platform first (Chrome and Edge: "Windows", "macOS",
 * "Android"...), then navigator.platform ("Win32", "MacIntel", "iPhone"),
 * then the user agent. An iPad asks for the desktop site and says it is a
 * Mac ("MacIntel", "Macintosh"); the touch screen gives it away - a Mac has
 * none - so a Mac with touch points is a tablet. Only a hint for what to
 * show: the server never decides anything on it.
 */
export function deviceOf(nav = typeof navigator === 'undefined' ? null : navigator) {
  if (!nav) return 'other';
  const hinted = String(nav.userAgentData?.platform || '').toLowerCase();
  const platform = String(nav.platform || '').toLowerCase();
  const ua = String(nav.userAgent || '').toLowerCase();
  const touch = Number(nav.maxTouchPoints || 0) > 1;
  if (nav.userAgentData?.mobile) return 'other';
  if (/iphone|ipad|ipod|android/.test(platform) || /iphone|ipad|ipod|android/.test(ua)) return 'other';
  const say = hinted || platform;
  if (say) {
    if (say.startsWith('win')) return 'windows';
    if (say.startsWith('mac')) return touch ? 'other' : 'mac';
    return 'other';
  }
  if (/windows nt/.test(ua)) return 'windows';
  if (/macintosh|mac os x/.test(ua)) return touch ? 'other' : 'mac';
  return 'other';
}

/** Is this browser on Windows? */
export const onWindows = () => deviceOf() === 'windows';

/**
 * Where a link shows: the server says (075), and before 075 it is worked out
 * from the scheme the way the server would.
 */
export function platformOf(link, schemes = []) {
  if (link.platform) return link.platform;
  const app = schemes.find((s) => s.scheme === schemeOf(link.url));
  return app?.platform || (app?.windows ? 'windows' : 'any');
}

/**
 * A shortcut's keys, as steps to draw: { press: ['cmd', 'shift', '5'] } or
 * { type: 'Calculator' }. The server has already checked them (cleanKeys in
 * routes/links.js); this only splits what it stored, and is forgiving with
 * what is being typed into the dialog.
 */
export function parseKeys(keys) {
  return String(keys || '').replace(/\s+/g, ' ').trim().split(/ then /i).filter(Boolean).map((step) => {
    const typed = /^"([^"]*)"?$/.exec(step);
    if (typed) return { type: typed[1] };
    return { press: step.toLowerCase().split(/ ?\+ ?/).filter(Boolean) };
  });
}

const CAPS = {
  mac: { cmd: ['⌘', 'Command'], shift: ['⇧', 'Shift'], option: ['⌥', 'Option'], alt: ['⌥', 'Option'], ctrl: ['⌃', 'Control'],
    fn: ['fn', 'Function'], return: ['↩', 'Return'], enter: ['↩', 'Return'], delete: ['⌫', 'Delete'], backspace: ['⌫', 'Delete'],
    esc: ['esc', 'Escape'], tab: ['⇥', 'Tab'], win: ['⊞', 'Windows'] },
  windows: { cmd: ['⌘', 'Command'], win: ['⊞ Win', 'Windows'], ctrl: ['Ctrl', 'Control'], alt: ['Alt', 'Alt'], option: ['Alt', 'Alt'],
    shift: ['Shift', 'Shift'], return: ['Enter', 'Enter'], enter: ['Enter', 'Enter'], esc: ['Esc', 'Escape'], tab: ['Tab', 'Tab'],
    delete: ['Del', 'Delete'], backspace: ['⌫', 'Backspace'], printscreen: ['PrtSc', 'Print Screen'] },
  any: { space: ['Space', 'Space'], up: ['↑', 'Up arrow'], down: ['↓', 'Down arrow'], left: ['←', 'Left arrow'], right: ['→', 'Right arrow'],
    home: ['Home', 'Home'], end: ['End', 'End'], pageup: ['PgUp', 'Page Up'], pagedown: ['PgDn', 'Page Down'] }
};

/** One key as drawn on its cap, and as said aloud: ['⌘', 'Command']. */
export function keyCap(key, platform = 'mac') {
  const k = String(key || '').toLowerCase();
  const hit = (CAPS[platform === 'windows' ? 'windows' : 'mac'] || {})[k] || CAPS.any[k];
  if (hit) return hit;
  if (/^f\d{1,2}$/.test(k)) return [k.toUpperCase(), k.toUpperCase()];
  return [k.toUpperCase(), k.length === 1 ? k.toUpperCase() : k];
}

/** The keys in words, for a screen reader and a tooltip: "Command Space, then type Calculator, then Return". */
export const keysInWords = (keys, platform = 'mac') => parseKeys(keys).map((s) => (s.type !== undefined
  ? `type ${s.type}`
  : s.press.map((k) => keyCap(k, platform)[1]).join(' '))).join(', then ');
